import React from 'react';
import { Box, Typography } from '@mui/joy';
import { useTheme } from '@mui/material/styles';
import { Trans, useTranslation } from 'react-i18next';
import { DescriptionFilledRounded } from '@mui-symbols-material/w300';
import { VACUUM_CHECK_TEMPLATE } from '../../config/vacuumCheckTemplate';
import { useAuthenticatedFileDownload } from '../../hooks/useAuthenticatedFileOpen';

/**
 * Inline link to the template, styled like the CSV import's "example.csv" link.
 * Trans replaces the slot element's children with the translated text, so the
 * icon lives in here rather than as a child at the call site.
 */
const TemplateLink = ({ color, onDownload, children }: { color: string; onDownload: () => void; children?: React.ReactNode }) => (
    <a
        // A real href keeps it a link for the browser; the click fetches with the JWT instead.
        href={VACUUM_CHECK_TEMPLATE.url}
        data-testid="vacuum-check-template-download"
        onClick={(event) => {
            event.preventDefault();
            onDownload();
        }}
        style={{
            display: 'inline-flex',
            alignItems: 'center',
            verticalAlign: 'middle',
            gap: '2px',
            textDecoration: 'none',
            color,
            fontWeight: 600,
            paddingBottom: '4px',
        }}
    >
        <DescriptionFilledRounded fontSize="small" style={{ verticalAlign: 'middle' }} />
        {children}
    </a>
);

/**
 * Instructions above the file dropzone of ASSET_VACUUM_CHECK: download the template,
 * fill it in, upload it. Styled like the hint of the installed base CSV import.
 */
const VacuumCheckFileInstructions = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const downloadFile = useAuthenticatedFileDownload();
    const onDownload = () => void downloadFile(VACUUM_CHECK_TEMPLATE.url, VACUUM_CHECK_TEMPLATE.fileName);

    return (
        <Box
            data-testid="vacuum-check-file-instructions"
            borderRadius="4px"
            border="1px solid #ccc"
            padding="12px 16px"
            marginBottom={2}
        >
            <Typography level="body-md">
                {/* One sentence with the link inline, so languages can reorder it. */}
                <Trans
                    t={t}
                    i18nKey="To document the vacuum check, download this <1>template</1>, fill it in and upload the completed form below."
                    components={{ 1: <TemplateLink color={theme.palette.primary.main} onDownload={onDownload} /> }}
                />
            </Typography>
        </Box>
    );
};

export default VacuumCheckFileInstructions;
